import { Body, Button } from '@assembly-js/design-system';
import { Menu, MenuItem } from '@material-ui/core';
import clsx from 'clsx';
import * as React from 'react';

import { useResizeObserver } from 'src/hooks/useResizeObserver';

interface Tab<T> {
  key: T;
  label: string | React.ReactNode;
  disabled?: boolean;
  icon?: React.ComponentType;
}

const MORE_TABS_BUTTON_WIDTH = 100;
const TAB_GAP = 8; // gap-2 = 8px (between tabs)
const CONTAINER_GAP = 12; // gap-3 = 12px (between tabs container and "N more")

interface ResponsiveTabsProps<T> {
  tabs: Array<Tab<T>>;
  activeTabKey: T | undefined;
  onTabChange: (newValue: T) => void;
  overflow?: 'scroll' | 'truncate';
  className?: string;
  classes?: {
    tabButton?: string;
  };
}

function getActiveItemClassName(isActive: boolean) {
  return [
    'border-[0]',
    'border-b',
    'border-solid',
    isActive ? 'border-gray-500' : 'border-transparent',
    isActive ? '' : 'text-secondary',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Base Tabs component renders tabs header
 * @returns {React.ReactElement}
 */
export const ResponsiveTabs = <T,>({
  activeTabKey,
  onTabChange,
  tabs,
  overflow,
  className,
  classes,
}: ResponsiveTabsProps<T>) => {
  const [tabGroupings, setTabGroupings] = React.useState<{
    primary: Tab<T>[];
    overflow: Tab<T>[];
  }>({
    primary: [],
    overflow: [],
  });
  // Store measured widths for each tab (keyed by tab key)
  const [tabWidthMap, setTabWidthMap] = React.useState<Map<T, number>>(
    new Map(),
  );
  const [availableWidth, setAvailableWidth] = React.useState<number>(0);
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = React.useState(false);
  const fallbackTab = tabs.length ? tabs.at(0)?.key : '';
  const activeTab = activeTabKey || fallbackTab;
  const overflowRef = React.useRef<HTMLDivElement | null>(null);
  const { ref: containerRef, rect: containerRect } =
    useResizeObserver<HTMLDivElement>();
  const hiddenContainerRef = React.useRef<HTMLDivElement | null>(null);

  const renderTabButton = (
    tab: Tab<T>,
    options: {
      isHidden?: boolean;
      onClick?: () => void;
    } = {},
  ) => {
    const { isHidden = false, onClick } = options;
    const labelText =
      typeof tab.label === 'string' ? tab.label : String(tab.label);

    return (
      <div key={String(tab.key)} data-tab-key={tab.key} className="h-full">
        <Button
          className={[
            'whitespace-nowrap rounded-none',
            'h-full px-2',
            getActiveItemClassName(activeTab === tab.key),
            classes?.tabButton,
          ].join(' ')}
          data-testid={isHidden ? undefined : `tab-${String(tab.key)}`}
          onClick={onClick}
          label={labelText}
          variant="text"
          tabIndex={isHidden ? -1 : undefined}
        />
      </div>
    );
  };

  React.useEffect(() => {
    const calculatePrimaryTabs = () => {
      if (!containerRef.current || !hiddenContainerRef.current) {
        return;
      }

      // when overflow is set to scroll, we want to show all tabs
      // without truncating the tabs into dropdown.
      if (overflow === 'scroll') {
        setTabGroupings({
          primary: tabs,
          overflow: [],
        });
        return;
      }

      const el = containerRef.current;
      // Use containerRect width from resize observer, falling back to getBoundingClientRect when observer hasn't fired yet (width is 0)
      const containerWidth =
        containerRect?.width ||
        el.getBoundingClientRect().width -
          parseFloat(getComputedStyle(el).paddingLeft) -
          parseFloat(getComputedStyle(el).paddingRight);

      if (!containerWidth) {
        return;
      }

      // Query tabs from the hidden container for measurement
      const tabEls = Array.from(
        hiddenContainerRef.current.querySelectorAll('[data-tab-key]'),
      );

      // Calculate total width of all tabs and store individual widths
      let totalTabsWidth = 0;
      const tabWidths: number[] = [];
      const newTabWidthMap = new Map<T, number>();

      tabEls.forEach((tabEl, index) => {
        const { width } = tabEl.getBoundingClientRect();
        tabWidths.push(width);
        totalTabsWidth += width;
        // Store width keyed by tab key
        const tabKey = tabs[index]?.key;
        if (tabKey !== undefined) {
          newTabWidthMap.set(tabKey, width);
        }
      });
      // Add gaps between tabs
      totalTabsWidth += (tabEls.length - 1) * TAB_GAP;

      // Store measurements for use in effectiveTabGroupings
      setTabWidthMap(newTabWidthMap);
      setAvailableWidth(containerWidth);

      // If all tabs fit, show them all without overflow
      if (totalTabsWidth <= containerWidth) {
        setTabGroupings({
          primary: tabs,
          overflow: [],
        });
        return;
      }

      // Calculate how many tabs fit with the "More" button
      let usedWidth = MORE_TABS_BUTTON_WIDTH + CONTAINER_GAP; // Reserve space for "N more" button + gap
      let lastFittingIndex = 0;

      for (let i = 0; i < tabWidths.length; i++) {
        const tabWidth = tabWidths[i];
        const gapWidth = i > 0 ? TAB_GAP : 0;

        if (usedWidth + tabWidth + gapWidth <= containerWidth) {
          usedWidth += tabWidth + gapWidth;
          lastFittingIndex = i + 1;
        } else {
          break;
        }
      }

      // Ensure at least one tab is always visible
      if (lastFittingIndex < 1) {
        lastFittingIndex = 1;
      }

      setTabGroupings({
        primary: tabs.slice(0, lastFittingIndex),
        overflow: tabs.slice(lastFittingIndex),
      });
    };

    calculatePrimaryTabs();
  }, [containerRef, hiddenContainerRef, containerRect, tabs, overflow]);

  const handleOverflowTabChange = (tabKey: T) => {
    onTabChange(tabKey);
    setIsOverflowMenuOpen(false);
  };

  React.useEffect(() => {
    if (tabs.length && tabGroupings.primary.length === 0) {
      setTabGroupings({
        primary: tabs,
        overflow: [],
      });
    }
  }, [tabs, tabGroupings]);

  // Implements a notion style tab rendering
  // In case of a lot of tabs, we want the active tab to always render
  // instead of being in the list.
  const effectiveTabGroupings = React.useMemo(() => {
    // Keep current behavior when there is no overflow.
    if (tabGroupings.overflow.length === 0) {
      return tabGroupings;
    }

    // Check if active tab is already visible in primary tabs
    const isActiveInPrimary = tabGroupings.primary.some(
      (tab) => tab.key === activeTab,
    );

    // If active tab is already visible, keep the width-based calculation
    if (isActiveInPrimary) {
      return tabGroupings;
    }

    // Active tab is in overflow - swap it into primary
    const activeTabItem = tabs.find((tab) => tab.key === activeTab);
    if (!activeTabItem) {
      return tabGroupings;
    }

    // Get the width of the selected tab
    const selectedTabWidth = tabWidthMap.get(activeTab as T) ?? 0;
    if (selectedTabWidth === 0 || availableWidth === 0) {
      // Keep at least the first tab always visible
      const newPrimary = [...tabGroupings.primary];
      if (newPrimary.length > 1) {
        newPrimary.pop();
        newPrimary.push(activeTabItem);
      }
      return {
        primary: newPrimary,
        overflow: tabGroupings.overflow,
      };
    }

    // Calculate how many tabs fit WITH the selected tab at the end
    // Reserve space for: "N more" button + gap + selected tab + gap before selected tab
    const reservedWidth =
      MORE_TABS_BUTTON_WIDTH + CONTAINER_GAP + selectedTabWidth + TAB_GAP;
    let usedWidth = reservedWidth;
    const fittingTabs: Tab<T>[] = [];

    // Iterate over ALL tabs (in order) except the selected one
    // This maximizes visible tabs when selecting a small tab from overflow
    for (const tab of tabs) {
      // Skip the selected tab - it will be added at the end
      if (tab.key === activeTab) {
        continue;
      }

      const tabWidth = tabWidthMap.get(tab.key) ?? 0;
      const gapWidth = fittingTabs.length > 0 ? TAB_GAP : 0;

      if (usedWidth + tabWidth + gapWidth <= availableWidth) {
        fittingTabs.push(tab);
        usedWidth += tabWidth + gapWidth;
      } else {
        break;
      }
    }

    // Ensure at least one tab is visible (even if it means overflow)
    if (fittingTabs.length === 0 && tabs.length > 1) {
      const firstNonSelectedTab = tabs.find((t) => t.key !== activeTab);
      if (firstNonSelectedTab) {
        const firstTabWidth = tabWidthMap.get(firstNonSelectedTab.key) ?? 0;
        if (usedWidth + firstTabWidth <= availableWidth) {
          fittingTabs.push(firstNonSelectedTab);
        }
      }
    }

    // Add the selected tab at the end (right before "N more")
    fittingTabs.push(activeTabItem);

    // Build overflow: all tabs not visible in primary (except selected tab is shown as last primary)
    // Selected tab also appears in overflow list (highlighted) so user can see it's selected
    const visibleKeys = new Set(fittingTabs.map((t) => t.key));
    const newOverflow: Tab<T>[] = [];

    for (const tab of tabs) {
      // Add to overflow if: not visible in primary, OR is the selected tab (stays in dropdown)
      if (!visibleKeys.has(tab.key) || tab.key === activeTab) {
        newOverflow.push(tab);
      }
    }

    return {
      primary: fittingTabs,
      overflow: newOverflow,
    };
  }, [activeTab, tabGroupings, tabs, tabWidthMap, availableWidth]);

  return (
    <>
      {/* Hidden container for measuring all tabs */}
      <div
        ref={hiddenContainerRef}
        className="invisible fixed flex gap-2"
        aria-hidden="true"
      >
        {tabs.map((tab) => renderTabButton(tab, { isHidden: true }))}
      </div>

      {/* Visible container */}
      <div
        className={[
          'min-w-[250px]',
          'flex-1',
          'flex',
          'px-3',
          'sm:px-0',
          'gap-3',
          'items-center',
          className,
        ].join(' ')}
        ref={containerRef}
      >
        <div
          className={[
            'flex',
            'flex-nowrap',
            'gap-2',
            'items-center',
            'h-full',
            'min-w-0',
            'overflow-hidden',
          ].join(' ')}
        >
          {effectiveTabGroupings.primary.map((tab) =>
            renderTabButton(tab, {
              onClick: () => onTabChange(tab.key),
            }),
          )}
        </div>

        {effectiveTabGroupings.overflow.length > 0 && (
          <>
            <div
              ref={overflowRef}
              key="more-tabs"
              className={[
                'h-full',
                'flex',
                'items-center',
                // Don't show active underline on "N more" since selected tab is visible in primary
                getActiveItemClassName(false),
              ].join(' ')}
            >
              <Button
                className={clsx('text-gray-500 whitespace-nowrap h-full', {
                  'bg-primary-hover': isOverflowMenuOpen,
                })}
                label={`${effectiveTabGroupings.overflow.length} more`}
                onClick={() => {
                  setIsOverflowMenuOpen(!isOverflowMenuOpen);
                }}
                suffixIcon="ChevronDown"
                variant="text"
              />
              <Menu
                open={isOverflowMenuOpen}
                anchorEl={overflowRef.current}
                onClose={() => setIsOverflowMenuOpen(false)}
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                {effectiveTabGroupings.overflow.map(
                  ({ key, label, icon: TabIcon }) => (
                    <MenuItem
                      className={
                        key === activeTab ? 'bg-gray-200 hover:bg-gray-200' : ''
                      }
                      key={String(key)}
                      value={String(key)}
                      onClick={() => handleOverflowTabChange(key)}
                    >
                      {TabIcon && (
                        <div className="mr-2 [&>svg]:w-3 [&>svg]:h-3">
                          <TabIcon />
                        </div>
                      )}
                      <Body size="sm">{label}</Body>
                    </MenuItem>
                  ),
                )}
              </Menu>
            </div>
          </>
        )}
      </div>
    </>
  );
};
